import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow
} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import {Link, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {getAllPaginatedContractsAction} from "../api/contract";
import {AppState} from "../store";
import {
    ContractDTO,
    ContractStatus,
    ContractType,
    getPresentableContractStatus,
    getPresenTableContractType,
    UserPermission,
    UserRole
} from "../api/dtos";
import {setLoadUsersTaskStatus} from "../features/settings/settingsSlice";
import UserLayout from "../components/UserLayout";
import * as XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {robotoRegularBase64} from "../shared/pdf-fonts";
import AddIcon from "@mui/icons-material/Add";
import Fab from "@mui/material/Fab";
import {CreateContractDialog} from "./create-contract/createContractDialog";
import ContractAction from "../actions/ContractAction";
import {DeleteContractDialog} from "./delete-contract/deleteContractDialog";
import {EditContractDialog} from "./edit-contract/EditContractDialog";
import {adminOrHasPermission, formatDate, NO_DATA} from "../shared/utils";
import {AddFileToContractDialog} from "./add-file-to-contract/add-file-to-contract";
import {AsyncTaskStatusType} from "../shared/dtos";
import ClearIcon from "@mui/icons-material/Clear";
import FormControl from "@mui/material/FormControl";
import {Autocomplete} from "@mui/lab";
import TextField from "@mui/material/TextField";
import {getAllCompaniesAction} from "../api/settings";
import {CreateTaskDialog} from "./create-task/createTaskDialog";

export const Contracts = () => {
    const dispatch: any = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const allPaginatedContracts = useSelector((state: AppState) => state.contract.allPaginatedContracts);
    const getAllPaginatedContractsStatus = useSelector((state: AppState) => state.contract.getAllPaginatedContractsStatus);
    const currentUser = useSelector((state: AppState) => state.auth.userWithToken?.user);
    const allCompanies = useSelector((state: AppState) => state.settings.allCompanies);
    const page = Number(searchParams.get("page") || "1");
    const [relatedCompanyFilter, setRelatedCompanyFilter] = useState<number | null>(null);
    const [relatedSideFilter, setRelatedSideFilter] = useState<string | null>(null);
    const [typeFilter, setTypeFilter] = useState<ContractType | null>(null);
    const [statusFilter, setStatusFilter] = useState<ContractStatus | null>(null);
    const [createContractDialogOpen, setCreateContractDialogOpen] = useState(false);
    const [activeContract, setActiveContract] = useState<ContractDTO | null>(null);
    const [deleteContractDialogOpen, setDeleteContractDialogOpen] = useState(false);
    const [editContractDialogOpen, setEditContractDialogOpen] = useState(false);

    const handleCreateContractDialogClose = () => setCreateContractDialogOpen(false);
    const handleCreateContractDialogOpen = () => setCreateContractDialogOpen(true);
    const openCreateContract = () => handleCreateContractDialogOpen();

    const handleDeleteContractDialogOpen = () => setDeleteContractDialogOpen(true);
    const handleDeleteContractDialogClose = () => setDeleteContractDialogOpen(false);

    const handleEditContractDialogOpen = () => setEditContractDialogOpen(true);
    const handleEditContractDialogClose = () => setEditContractDialogOpen(false);

    useEffect(() => {
        if (currentUser) {
            let relatedCompanyId = currentUser.associatedCompany ? currentUser.associatedCompany.id : null;

            dispatch(getAllPaginatedContractsAction({
                relatedCompanyId: relatedCompanyFilter || relatedCompanyId,
                type: typeFilter,
                status: statusFilter,
                page,
                relatedSide: relatedSideFilter,
            }));

            return () => {
                dispatch(setLoadUsersTaskStatus(null));
            };
        }
    }, [dispatch, page, typeFilter, statusFilter, relatedCompanyFilter, relatedSideFilter, currentUser?.id, currentUser?.role]);

    const handleChangePage = (_: React.ChangeEvent<unknown>, value: number) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("page", value.toString());
        navigate(`?${searchParams.toString()}`);
    };

    const reloadFirstPage = () => {
        let relatedCompanyId = null;
        if (currentUser) {
            if (currentUser.associatedCompany) {
                relatedCompanyId = currentUser.associatedCompany.id;
            }
            if (page === 1) {
                dispatch(
                    getAllPaginatedContractsAction({
                        relatedCompanyId: relatedCompanyFilter || relatedCompanyId,
                        type: typeFilter,
                        status: statusFilter,
                        page,
                        relatedSide: relatedSideFilter,
                    })
                );
            } else {
                navigate("/sozlesmeler?page=1");
            }
        }
    };

    const handleCreateContract = () => {
        setTypeFilter(null);
        setStatusFilter(null);
        reloadFirstPage();
    };

    const openDelete = (contract: ContractDTO) => {
        setActiveContract(contract);
        handleDeleteContractDialogOpen();
    };

    const openEdit = (contract: ContractDTO) => {
        setActiveContract(contract);
        handleEditContractDialogOpen();
    };

    const handleDelete = () => {
        setActiveContract(null);
        handleDeleteContractDialogClose();
        reloadFirstPage();
    };

    const handleEdit = () => {
        setActiveContract(null);
        handleEditContractDialogClose();
        reloadFirstPage();
    };

    const [addFileDialogOpen, setAddFileDialogOpen] = useState(false);

    const handleAddFileDialogOpen = () => setAddFileDialogOpen(true);
    const handleAddFileDialogClose = () => setAddFileDialogOpen(false);
    const [addTaskDialogOpen, setAddTaskDialogOpen] = useState(false);
    const handleAddTaskDialogOpen = () => setAddTaskDialogOpen(true);
    const handleAddTaskDialogClose = () => setAddTaskDialogOpen(false);

    const openAddFile = (contract: ContractDTO) => {
        setActiveContract(contract);
        handleAddFileDialogOpen();
    };

    const handleFileAdded = () => {
        setActiveContract(null);
        handleAddFileDialogClose();
    };

    const handleCreateTask = () => {
        setActiveContract(null);
        handleAddTaskDialogClose();
        reloadFirstPage();
    }

    const openAddTask = (contract: ContractDTO) => {
        setActiveContract(contract);
        setAddTaskDialogOpen(true);
    };

    const exportToExcel = () => {
        const headers = [
            ["İlgili Taraf", "Sözleşme Türü", "Başlangıç Tarihi", "Geçerlilik Tarihi", "İlgili Şube/İştirak", "Durum"]
        ];
        const worksheet = XLSX.utils.json_to_sheet(
            (allPaginatedContracts?.entity || []).map((contract) => ({
                "İlgili Taraf": contract.relatedSide,
                "Sözleşme Türü": getPresenTableContractType(contract.type),
                "Başlangıç Tarihi": formatDate(contract.signDate),
                "Geçerlilik Tarihi": contract.validUntil
                    ? formatDate(contract.validUntil)
                    : "Süresiz",
                "İlgili Şube/İştirak": contract.relatedCompany.name,
                "Durum": getPresentableContractStatus(contract.status),
            }))
        );

        const workbook = XLSX.utils.book_new();
        XLSX.utils.sheet_add_aoa(worksheet, headers);
        const headerRow = headers[0];
        const columnWidths = headerRow.map(header => ({wch: header.length + 5}));
        worksheet['!cols'] = columnWidths;
        headerRow.forEach((_, index) => {
            const cellRef = XLSX.utils.encode_cell({r: 0, c: index});
            if (worksheet[cellRef]) {
                worksheet[cellRef].s = {
                    font: {bold: true},
                    fill: {
                        patternType: "solid",
                        fgColor: {rgb: "D3D3D3"}
                    }
                };
            }
        });

        XLSX.utils.book_append_sheet(workbook, worksheet, "Sözleşmeler");
        XLSX.writeFile(workbook, "sözleşmeler_listesi.xlsx");
    };

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.addFileToVFS("Roboto-Regular.ttf", robotoRegularBase64);
        doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");
        doc.setFont("Roboto");

        doc.setFontSize(18);
        doc.text("Sözleşmeler", 14, 20);

        const tableData = (allPaginatedContracts?.entity || []).map((contract) => [
            contract.relatedSide,
            getPresenTableContractType(contract.type),
            formatDate(contract.signDate),
            contract.validUntil
                ? formatDate(contract.validUntil)
                : "Süresiz",
            contract.relatedCompany.name,
            getPresentableContractStatus(contract.status),
        ]);

        const tableColumns = ["İlgili Taraf", "Sözleşme Türü", "Başlangıç Tarihi", "Geçerlilik Tarihi", "İlgili Şube/İştirak", "Durum"];

        autoTable(doc, {
            head: [tableColumns],
            body: tableData,
            startY: 30,
            margin: {horizontal: 10},
            styles: {
                font: "Roboto",
                cellPadding: 3,
                fontSize: 8,
                valign: "middle",
                halign: "left",
            },
        });

        doc.save("sözleşmeler_listesi.pdf");
    };

    const getContractDetailUrl = (contract: ContractDTO) => {
        return `/sozlesmeler/${contract.id}/ayrintilar`;
    };

    const applyTypeFilter = (e: { target: { value: string; }; }) => {
        const type = e.target.value === "" ? null : (e.target.value as ContractType);
        setTypeFilter(type);
        navigate("?page=1");
    };

    const applyStatusFilter = (e: { target: { value: string; }; }) => {
        const status = e.target.value === "" ? null : (e.target.value as ContractStatus);
        setStatusFilter(status);
        navigate("?page=1");
    };

    const applyRelatedCompanyFilter = (event: any, newValue: any) => {
        setRelatedCompanyFilter(newValue ? newValue.id : null);
        navigate("?page=1");
    };

    const applyRelatedSideFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRelatedSideFilter(e.target.value);
        navigate("?page=1");
    };

    const handleClearType = () => {
        setTypeFilter(null);
    };

    const handleClearStatus = () => {
        setStatusFilter(null);
    };

    const typeOptions = Object.values(ContractType);
    const statusOptions = Object.values(ContractStatus);

    useEffect(() => {
        dispatch(getAllCompaniesAction());
    }, [dispatch]);

    /*
        {adminOrHasPermission(currentUser, [UserPermission.CONTRACT_LIST])
        ? (
            <Link to={getContractDetailUrl(contract)} className="custom-link">
                {contract.relatedSide || NO_DATA}
            </Link>
        )
        : contract.relatedSide || NO_DATA}
    * */

    return (
        <UserLayout>
            <div id="sidebar">
                <h1>SÖZLEŞMELER</h1>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 1,
                        m: 1,
                        borderRadius: 1,
                    }}>
                    <Box sx={{display: "flex", gap: 2}}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={exportToExcel}
                            sx={{fontSize: "0.875rem", padding: "8px 16px"}}>
                            Excel'e Dışa Aktar
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={exportToPDF}
                            sx={{fontSize: "0.875rem", padding: "8px 16px"}}>
                            PDF'e Dışa Aktar
                        </Button>
                    </Box>
                    {adminOrHasPermission(currentUser, UserPermission.CONTRACT_ADD) && (
                        <Fab
                            size="small"
                            color="primary"
                            aria-label="add"
                            sx={{marginLeft: "auto", marginBottom: 5}}
                            onClick={openCreateContract}>
                            <AddIcon/>
                        </Fab>
                    )}
                </Box>
            </div>

            {createContractDialogOpen && (
                <CreateContractDialog
                    onClose={handleCreateContractDialogClose}
                    onContractAdded={handleCreateContract}/>
            )}

            {deleteContractDialogOpen && activeContract != null && (
                <DeleteContractDialog
                    contract={activeContract}
                    handleClose={handleDeleteContractDialogClose}
                    handleDelete={handleDelete}/>
            )}

            {editContractDialogOpen && activeContract != null && (
                <EditContractDialog
                    contract={activeContract}
                    handleClose={handleEditContractDialogClose}
                    handleEdit={handleEdit}/>
            )}

            {addFileDialogOpen && activeContract != null && (
                <AddFileToContractDialog
                    contract={activeContract}
                    onClose={handleAddFileDialogClose}
                    onFileAdded={handleFileAdded}/>
            )}

            {addTaskDialogOpen && activeContract && (
                <CreateTaskDialog
                    contractId={activeContract.id}
                    handleClose={handleAddTaskDialogClose}
                    handleCreateTask={handleCreateTask}/>
            )}

            <Box sx={{overflowX: 'auto'}}>
                <Table sx={{minWidth: 800}} aria-label="my contracts table">
                    <TableHead>
                        <TableRow>
                            <TableCell style={{width: "22%"}}>
                                {currentUser?.role !== UserRole.Company && (
                                    <FormControl variant="outlined" size="small" fullWidth>
                                        <Autocomplete
                                            id="related-company-filter"
                                            options={allCompanies || []}
                                            getOptionLabel={(option) => option.name}
                                            value={allCompanies?.find(company => company.id === relatedCompanyFilter) || null}
                                            onChange={applyRelatedCompanyFilter}
                                            clearOnEscape
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="İlgili Şirket"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: params.InputProps.endAdornment,
                                                    }}/>
                                            )}/>
                                    </FormControl>
                                )}
                            </TableCell>
                            <TableCell style={{width: "22%"}}>
                                <FormControl variant="outlined" size="small" fullWidth>
                                    <TextField
                                        label="İlgili Taraf"
                                        value={relatedSideFilter || ""}
                                        onChange={applyRelatedSideFilter}
                                        InputProps={{
                                            endAdornment: relatedSideFilter && (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setRelatedSideFilter(null)}>
                                                        <ClearIcon/>
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}/>
                                </FormControl>
                            </TableCell>
                            <TableCell style={{width: "12%"}}>
                                <FormControl variant="outlined" size="small" fullWidth>
                                    <InputLabel id="type-label">Sözleşme Türü</InputLabel>
                                    <Select
                                        style={{minWidth: "210px"}}
                                        labelId="type-label"
                                        label="Sözleşme Türü"
                                        value={typeFilter || ""}
                                        onChange={applyTypeFilter}
                                        defaultValue=""
                                        endAdornment={
                                            typeFilter && (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleClearType}>
                                                        <ClearIcon/>
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }>
                                        {typeOptions.map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {getPresenTableContractType(type)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </TableCell>
                            <TableCell style={{width: "12%"}}/>
                            <TableCell style={{width: "12%"}}/>
                            <TableCell style={{width: "12%"}}>
                                <FormControl variant="outlined" size="small" fullWidth>
                                    <InputLabel id="status-label">Durumu</InputLabel>
                                    <Select
                                        style={{minWidth: "210px"}}
                                        labelId="status-label"
                                        label="Durumu"
                                        value={statusFilter || ""}
                                        onChange={applyStatusFilter}
                                        defaultValue=""
                                        endAdornment={
                                            statusFilter && (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleClearStatus}>
                                                        <ClearIcon/>
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }>
                                        {statusOptions.map((status) => (
                                            <MenuItem key={status} value={status}>
                                                {getPresentableContractStatus(status)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </TableCell>
                            <TableCell style={{width: "12%"}}/>
                        </TableRow>
                        <TableRow>
                            <TableCell>İLGİLİ ŞUBE</TableCell>
                            <TableCell>İLGİLİ TARAF</TableCell>
                            <TableCell>SÖZLEŞME TÜRÜ</TableCell>
                            <TableCell>BAŞLANGIÇ TARİHİ</TableCell>
                            <TableCell>GEÇERLİLİK TARİHİ</TableCell>
                            <TableCell>DURUM</TableCell>
                            <TableCell>İŞLEMLER</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {allPaginatedContracts?.entity.map((contract) => (
                            <TableRow key={contract.id}>
                                <TableCell>{contract.relatedCompany.name}</TableCell>
                                <TableCell>
                                    <Link to={getContractDetailUrl(contract)} className="custom-link">
                                        {contract.relatedSide || NO_DATA}
                                    </Link>
                                </TableCell>
                                <TableCell>{getPresenTableContractType(contract.type)}</TableCell>
                                <TableCell>
                                    {contract.signDate
                                        ? formatDate(contract.signDate)
                                        : NO_DATA}
                                </TableCell>
                                <TableCell>
                                    {contract.validUntil
                                        ? formatDate(contract.validUntil)
                                        : "Süresiz"}
                                </TableCell>
                                <TableCell>{getPresentableContractStatus(contract.status)}</TableCell>
                                <TableCell>
                                    <ContractAction
                                        onAddTask={openAddTask}
                                        onEdit={openEdit}
                                        contract={contract}
                                        onDelete={openDelete}
                                        onAddFile={openAddFile}/>
                                </TableCell>
                            </TableRow>
                        ))}
                        {getAllPaginatedContractsStatus?.type !== AsyncTaskStatusType.Loading && allPaginatedContracts?.numberOfElements === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} style={{textAlign: "center"}}>
                                    Sözleşmeler Bulunamadı
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>
            {allPaginatedContracts?.totalNumberPages != null && allPaginatedContracts.totalNumberPages > 1 && (
                <div style={{display: "flex", justifyContent: "center", marginTop: "10px"}}>
                    <Pagination page={page} count={allPaginatedContracts.totalNumberPages} defaultPage={1}
                                onChange={handleChangePage}/>
                </div>
            )}
        </UserLayout>
    );
};
